import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { gridPaths } from '@/shared/hooks';
import { createEntityGridConfig, type GridConfig } from '@/ui/composites/grid/gridConfigFactory';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { fetchSuiteRunsGrid } from '../grid/suiteRunsGridQuery';
import { fetchTestRunsGrid } from '../grid/testRunsGridQuery';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridFilterModel, GridSortModel } from '@/ui/primitives';
import type { SuiteRunDto, TestRunDto } from '@flowqa/shared';

type ExecutionGridConfig<TDto> = GridConfig<TDto>;

export const suiteRunDefaultSortModel: GridSortModel = [{ field: 'timestamp', sort: 'desc' }];
export const testRunDefaultSortModel: GridSortModel = [{ field: 'timestamp', sort: 'desc' }];

export const buildSuiteRunFilters = (suiteId: string): GridQueryInput['filters'] =>
  buildEqFilter('suiteId', suiteId);

export const buildTestRunFilters = (testId: string): GridQueryInput['filters'] =>
  buildEqFilter('testId', testId);

export const suiteRunsGridConfig = ({
  projectId,
  organisationId,
  fixedFilters,
  includeSummary = false,
  initialSortModel = suiteRunDefaultSortModel,
  initialFilterModel,
  persistKey,
  syncStateToUrl = false,
}: {
  projectId?: string | null;
  organisationId?: string | null;
  fixedFilters?: GridQueryInput['filters'];
  includeSummary?: boolean;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
} = {}): ExecutionGridConfig<SuiteRunDto> =>
  createEntityGridConfig<SuiteRunDto>({
    path: gridPaths.suiteRuns,
    queryFn: async (input) =>
      await fetchSuiteRunsGrid(input, projectId, organisationId, includeSummary),
    noRowsText: getEmptyStateMessage('entity.noneFound', { entity: 'suite' }),
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    defaultPersistKey: 'suite-runs',
    syncStateToUrl,
  });

export const testRunsGridConfig = ({
  projectId,
  organisationId,
  fixedFilters,
  initialSortModel = testRunDefaultSortModel,
  initialFilterModel,
  persistKey,
  syncStateToUrl = false,
}: {
  projectId?: string | null;
  organisationId?: string | null;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
} = {}): ExecutionGridConfig<TestRunDto> =>
  createEntityGridConfig<TestRunDto>({
    path: gridPaths.testRuns,
    queryFn: async (input) => await fetchTestRunsGrid(projectId, organisationId, input),
    noRowsText: getEmptyStateMessage('entity.noneFound', { entity: 'test' }),
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    defaultPersistKey: 'test-runs',
    syncStateToUrl,
  });

export type SuiteRunsGridConfig = ReturnType<typeof suiteRunsGridConfig>;
export type TestRunsGridConfig = ReturnType<typeof testRunsGridConfig>;
